import React, { useState, useEffect, useRef } from 'react';
// Import useLocation from react-router-dom
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// Ensure path to logo is correct
import logo from "../../../img/plasticAnalyticsLogo.png";

const Header = ({ plasticAnalyticsLogo = logo, plasticAnalyticsNavLogo }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null); // Tracks which dropdown is open by label
  const [isMenuOpen, setIsMenuOpen] = useState(false); // *** THIS CONTROLS MOBILE MENU ***
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const dropdownRef = useRef(null); // Ref for desktop nav container
  const lastScrollY = useRef(0);
  const scrollTimer = useRef(null);
  const mobileBreakpoint = 1024;
  const scrollThreshold = 50; // Pixels scrolled before hiding/showing header
  const navigate = useNavigate(); // For programmatic navigation

  // *** State and hook for tracking active section based on URL ***
  const [activeSectionKey, setActiveSectionKey] = useState(null); // Tracks the key ('about', 'spectralLibrary', etc.)
  const location = useLocation(); // Get location object from router

  // *** State for generic button hover effects ***
  const [hoveredButton, setHoveredButton] = useState(null); // Tracks 'signin', 'hamburger', etc.

  // Define navigation sections
  const aboutUsDropdownItems = [
    { label: 'About', path: '/about' },
    { label: 'News', path: '/news' },
    { label: 'Contact Us', path: '/contact-us' },
  ];

  const spectralLibraryDropdownItems = [
    { label: 'Spectral Database', path: '/spectral-library/database' },
    { label: 'Analyse Spectra', path: '/spectral-library/analyse' },
    { label: 'Upload Spectra', path: '/spectral-library/upload' },
  ];

  const navigationItems = {
    about: { label: "About", items: aboutUsDropdownItems },
    spectralLibrary: { label: "Spectral Library", items: spectralLibraryDropdownItems },
    pricing: { label: "Pricing", path: "/pricing", items: [] }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setWindowWidth(currentWidth);
      if (currentWidth > mobileBreakpoint && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Handle scroll visibility and sticky state
  useEffect(() => {
    const handleScroll = () => {
      // Prevent scroll logic execution when mobile menu is open
      if (isMenuOpen) {
        // Ensure header stays visible while menu is open (optional safeguard)
         if (!isVisible) setIsVisible(true);
         lastScrollY.current = window.scrollY; // Update last scroll pos to prevent jump on close
        return;
      }

      const currentScrollY = window.scrollY;
      if (currentScrollY <= 5) {
        setIsVisible(true);
        setIsSticky(false);
        lastScrollY.current = currentScrollY;
        clearTimeout(scrollTimer.current);
        scrollTimer.current = null;
        return;
      }
      setIsSticky(true);
      const scrollDifference = currentScrollY - lastScrollY.current;

      if (scrollDifference < -scrollThreshold / 2) { // Scrolling Up
        clearTimeout(scrollTimer.current);
        scrollTimer.current = null;
        if (!isVisible) setIsVisible(true);
      } else if (scrollDifference > scrollThreshold && isVisible) { // Scrolling Down (and visible)
        clearTimeout(scrollTimer.current);
        scrollTimer.current = setTimeout(() => {
          // Re-check after delay, only hide if still lower or same pos
          if (window.scrollY >= lastScrollY.current) {
             setIsVisible(false);
          }
        }, 250);
      }
      lastScrollY.current = currentScrollY;
    };

    // Conditionally add/remove listener based on menu state
    // This avoids potential race conditions when menu closes and scroll triggers immediately
    if (!isMenuOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      // Ensure header is visible when menu opens, if it was hidden
      if (!isVisible) setIsVisible(true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer.current);
    };
  }, [isMenuOpen, isVisible]); // Dependencies

  // Handle clicks outside DESKTOP dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Effect to determine Active Section based on URL
  useEffect(() => {
    const pathname = location.pathname;
    let currentActiveKey = null;
    if (pathname.startsWith('/about') || pathname.startsWith('/news') || pathname.startsWith('/contact-us')) {
      currentActiveKey = 'about';
    } else if (pathname.startsWith('/spectral-library')) {
      currentActiveKey = 'spectralLibrary';
    } else if (pathname.startsWith('/pricing')) {
      currentActiveKey = 'pricing';
    }
    setActiveSectionKey(currentActiveKey);
  }, [location.pathname]);

  // --- CORRECTED Mobile Menu Toggle Handler ---
  const handleMenuToggle = (e) => {
    // Prevent default button behavior and event bubbling
    e.preventDefault();
    e.stopPropagation();
    // Simply toggle the menu state
    setIsMenuOpen(prevState => !prevState);
    // DO NOT call setIsVisible here - let the scroll effect handle it.
  };
  
  // Handle navigation to prevent Link-related issues
  const handleNavigation = (path, e) => {
    e.preventDefault();
    // Close any open dropdown or menu
    setActiveDropdown(null);
    setIsMenuOpen(false);
    // Navigate programmatically 
    navigate(path);
  };

  // Colors
  const colors = {
    primary: 'white',
    blue: 'rgb(26, 42, 108)',
    orange: 'rgb(230, 126, 34)',
    text: '#333333',
    lightGray: '#f5f5f5',
    border: '#e5e7eb'
  };

  // Styles Object (Inline JS Styles)
  const styles = {
    utilityHeader: {
      display: windowWidth > mobileBreakpoint ? 'block' : 'none',
      backgroundColor: colors.primary,
      borderBottom: `1px solid ${colors.border}`,
      padding: '8px 0',
    },
    utilityContainer: {
        maxWidth: '1280px', margin: '0 auto', padding: '0 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px'
    },
    stockInfo: { display: 'flex', alignItems: 'center', gap: '8px' },
    stockSymbol: { color: '#4B5563' }, stockChange: { color: '#EF4444' }, stockPrice: { color: '#374151' },
    utilityLinks: { display: 'flex', gap: '24px' },
    utilityLink: { color: '#4B5563', textDecoration: 'none', transition: 'color 0.2s ease', cursor: 'pointer' },
    mainHeader: {
      position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1000,
      transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 0.4s ease-in-out',
      padding: windowWidth > mobileBreakpoint ? '15px 0' : '10px 0',
      boxSizing: 'border-box',
    },
    mainContainer: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      maxWidth: '1600px', margin: '0 auto', padding: windowWidth > mobileBreakpoint ? '0 35px' : '0 15px',
      height: windowWidth > mobileBreakpoint ? '70px' : '60px',
      backgroundColor: colors.primary, borderRadius: '40px',
      boxShadow: isSticky ? '0 4px 15px rgba(0, 0, 0, 0.1)' : 'none',
      transition: 'box-shadow 0.3s ease', boxSizing: 'border-box',
    },
    logoContainer: { display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 },
    logo: { height: windowWidth > mobileBreakpoint ? '40px' : '30px', width: 'auto', display: 'block' },
    logoText: {
      fontSize: windowWidth > mobileBreakpoint ? '16px' : '0', color: colors.blue, fontWeight: '600', marginLeft: '10px',
      transition: 'opacity 0.3s ease, width 0.3s ease', opacity: windowWidth > mobileBreakpoint ? 1 : 0,
      width: windowWidth > mobileBreakpoint ? 'auto' : '0', whiteSpace: 'nowrap', overflow: 'hidden',
    },
    nav: { display: windowWidth > mobileBreakpoint ? 'flex' : 'none', alignItems: 'center', gap: '32px', flexGrow: 1, justifyContent: 'center' },
    navLinks: { display: 'flex', alignItems: 'center', gap: '32px' },
    navItemBase: {
      color: colors.blue, textDecoration: 'none', padding: '8px 4px', fontSize: '16px', fontWeight: '500',
      position: 'relative', transition: 'color 0.2s ease', display: 'flex', alignItems: 'center', gap: '4px',
      cursor: 'pointer', background: 'none', border: 'none', whiteSpace: 'nowrap',
    },
    navItemActive: { color: colors.orange },
    navItemHover: { color: colors.orange },
    chevronIcon: { width: '14px', height: '14px', transition: 'transform 0.2s ease', marginLeft: '2px', color: 'currentColor', flexShrink: 0 },
    dropdownContentWrapper: { position: 'absolute', top: 'calc(100% + 10px)', left: '50%', zIndex: 1010, minWidth: '220px' },
    dropdownContent: {
      transform: 'translateX(-50%)', backgroundColor: 'white', boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
      borderRadius: '10px', padding: '8px 0', overflow: 'hidden',
    },
    dropdownItemBase: {
      display: 'block', padding: '10px 20px', color: colors.blue, textDecoration: 'none', fontSize: '15px',
      whiteSpace: 'nowrap', transition: 'background-color 0.2s ease, color 0.2s ease', textAlign: 'left',
      width: '100%', boxSizing: 'border-box', cursor: 'pointer',
    },
    dropdownItemHover: { backgroundColor: colors.lightGray, color: colors.orange },
    actionsContainer: { display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 },
    signInButtonBase: {
      padding: '10px 24px', backgroundColor: colors.blue, color: 'white', borderRadius: '24px', border: 'none',
      cursor: 'pointer', fontWeight: '600', fontSize: '15px', boxShadow: '0 4px 12px rgba(26, 42, 108, 0.15)',
      transition: 'all 0.3s ease', display: windowWidth > mobileBreakpoint ? 'block' : 'none',
    },
    signInButtonHover: { backgroundColor: colors.orange, transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(230, 126, 34, 0.3)' },
    hamburger: {
      backgroundColor: 'transparent', border: 'none', padding: '8px', display: windowWidth <= mobileBreakpoint ? 'flex' : 'none',
      justifyContent: 'center', alignItems: 'center', cursor: 'pointer', fontSize: '28px', color: colors.blue, transition: 'color 0.2s ease',
    },
    hamburgerHover: { color: colors.orange },
    mobileMenuContainer: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: colors.primary,
        zIndex: 1100, display: 'flex', flexDirection: 'column',
    },
    mobileMenuHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '15px 20px', borderBottom: `1px solid ${colors.border}`, flexShrink: 0,
    },
    mobileMenuLogo: { height: '30px', width: 'auto' },
    closeButton: { padding: '8px', fontSize: '28px', background: 'none', border: 'none', cursor: 'pointer', color: colors.blue, lineHeight: 1 },
    mobileMenuContent: { overflowY: 'auto', flexGrow: 1, paddingBottom: '20px' },
    mobileNavSection: { width: '100%', borderBottom: `1px solid ${colors.border}` },
    mobileNavButton: {
        width: '100%', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: '18px', fontWeight: '500', color: colors.blue, background: 'none', border: 'none',
        cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
    },
    mobileChevron: { width: '16px', height: '16px', transition: 'transform 0.2s ease' },
    mobileNavLinksContainer: { padding: '0 20px 16px 40px', backgroundColor: colors.lightGray },
    mobileNavLink: { display: 'block', padding: '12px 0', fontSize: '16px', color: colors.text, textDecoration: 'none', transition: 'color 0.2s ease' },
    mobileNavLinkHover: { color: colors.orange },
    mobileSignInLink: {
        width: '100%', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: '18px', fontWeight: '500', color: colors.blue, background: 'none', border: 'none',
        cursor: 'pointer', textAlign: 'left', textDecoration: 'none',
    }
  };

  // --- Dropdown Menu Component (Nested within Header) ---
  const DropdownMenu = ({ section, isActive, activeDropdown, setActiveDropdown }) => {
    // Use state only for desktop hover
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const isOpen = activeDropdown === section.label;
    
    // Only use hover if we're on desktop
    const handleMouseEnter = () => {
      if (windowWidth > mobileBreakpoint) {
        setIsHovered(true);
      }
    };
    
    const handleMouseLeave = () => {
      if (windowWidth > mobileBreakpoint) {
        setIsHovered(false);
      }
    };

    const handleDropdownToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setActiveDropdown(isOpen ? null : section.label);
    };

    const navItemStyle = { ...styles.navItemBase, ...(isHovered && !isActive ? styles.navItemHover : {}), ...(isActive ? styles.navItemActive : {}) };
    const chevronStyle = { ...styles.chevronIcon, color: navItemStyle.color };

    return (
      <div style={{ position: 'relative' }}>
        {section.path ? (
          <a 
            href={section.path} 
            style={navItemStyle} 
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={handleMouseLeave} 
            onClick={(e) => handleNavigation(section.path, e)}
          >
            {section.label}
          </a>
        ) : (
          <>
            <button 
              style={navItemStyle} 
              onMouseEnter={handleMouseEnter} 
              onMouseLeave={handleMouseLeave} 
              onClick={handleDropdownToggle} 
              aria-haspopup="true" 
              aria-expanded={isOpen}
            >
              {section.label}
              {section.items?.length > 0 && (
                 <svg style={{ ...chevronStyle, transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                   <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                 </svg>
              )}
            </button>
            <AnimatePresence>
              {isOpen && section.items?.length > 0 && (
                 <div style={styles.dropdownContentWrapper}>
                     <motion.div 
                       style={styles.dropdownContent} 
                       initial={{ opacity: 0, y: -10 }} 
                       animate={{ opacity: 1, y: 0 }} 
                       exit={{ opacity: 0, y: -10, transition: { duration: 0.15 } }} 
                       transition={{ duration: 0.2, ease: "easeOut" }}
                     >
                      {section.items.map((item, index) => (
                        <a
                          key={item.path}
                          href={item.path}
                          style={hoveredItem === index ? 
                            {...styles.dropdownItemBase, ...styles.dropdownItemHover} : 
                            styles.dropdownItemBase
                          }
                          onClick={(e) => handleNavigation(item.path, e)}
                          onMouseEnter={() => windowWidth > mobileBreakpoint && setHoveredItem(index)}
                          onMouseLeave={() => windowWidth > mobileBreakpoint && setHoveredItem(null)}
                        >
                          {item.label}
                        </a>
                      ))}
                     </motion.div>
                 </div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    );
  };

  // --- Mobile Menu Section Component (Nested within Header) ---
  const MobileNavSection = ({ title, items, path, onClose }) => {
    const [isSectionOpen, setIsSectionOpen] = useState(false);
    const [hoveredLink, setHoveredLink] = useState(null);

    if (path) {
        return (
          <div style={styles.mobileNavSection}>
            <a 
              href={path} 
              style={styles.mobileNavButton} 
              onClick={(e) => {
                handleNavigation(path, e);
                onClose();
              }}
            >
              {title}
            </a>
          </div>
        )
    }
    if (!items?.length) return null;

    return (
      <div style={styles.mobileNavSection}>
        <button 
          style={styles.mobileNavButton} 
          onClick={() => setIsSectionOpen(!isSectionOpen)} 
          aria-expanded={isSectionOpen}
        >
          {title}
          <svg style={{ ...styles.mobileChevron, transform: isSectionOpen ? 'rotate(180deg)' : 'rotate(0)' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
             <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        <AnimatePresence>
         {isSectionOpen && (
            <motion.div 
              style={styles.mobileNavLinksContainer} 
              initial={{ height: 0, opacity: 0 }} 
              animate={{ height: 'auto', opacity: 1 }} 
              exit={{ height: 0, opacity: 0 }} 
              transition={{ duration: 0.2 }}
            >
                {items.map((item, index) => (
                  <a 
                    key={item.path} 
                    href={item.path} 
                    style={
                      hoveredLink === index ? 
                      {...styles.mobileNavLink, ...styles.mobileNavLinkHover} : 
                      styles.mobileNavLink
                    } 
                    onClick={(e) => {
                      handleNavigation(item.path, e);
                      onClose();
                    }}
                    // Only use hover state on desktop
                    onMouseEnter={() => windowWidth > mobileBreakpoint && setHoveredLink(index)} 
                    onMouseLeave={() => windowWidth > mobileBreakpoint && setHoveredLink(null)}
                  >
                    {item.label}
                  </a>
                ))}
            </motion.div>
         )}
        </AnimatePresence>
      </div>
    );
  };

  // --- Mobile Menu Component (Nested within Header) ---
  const MobileMenu = ({ isOpen, onClose }) => {
    const [hoveredClose, setHoveredClose] = useState(false);
    const [hoveredSignIn, setHoveredSignIn] = useState(false);
    
    // Handle hover only on desktop for consistency
    const handleMouseEnter = (state) => {
      if (windowWidth > mobileBreakpoint) {
        state(true);
      }
    };
    
    const handleMouseLeave = (state) => {
      if (windowWidth > mobileBreakpoint) {
        state(false);
      }
    };

    useEffect(() => {
        const body = document.body;
        if (isOpen) {
            const previousOverflow = body.style.overflow;
            body.style.overflow = 'hidden';
            return () => { body.style.overflow = previousOverflow || ''; };
        }
    }, [isOpen]);

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            key="mobile-menu" 
            style={styles.mobileMenuContainer} 
            initial={{ opacity: 0, x: "-100%" }} 
            animate={{ opacity: 1, x: 0 }} 
            exit={{ opacity: 0, x: "-100%", transition: { duration: 0.2 } }} 
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div style={styles.mobileMenuHeader}>
              <a 
                href="/" 
                onClick={(e) => {
                  handleNavigation('/', e);
                  onClose();
                }} 
                style={{ lineHeight: 0 }}
              >
                <img src='https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Flogo.png?alt=media&token=e2c15ffa-4af0-4f55-af64-545ae75fe511' style={styles.mobileMenuLogo} alt="Plastic Analytics Logo" />
              </a>
              <button 
                onClick={onClose} 
                style={{
                  ...styles.closeButton, 
                  color: hoveredClose ? colors.orange : colors.blue
                }} 
                onMouseEnter={() => handleMouseEnter(setHoveredClose)} 
                onMouseLeave={() => handleMouseLeave(setHoveredClose)} 
                aria-label="Close menu"
              >
                ×
              </button>
            </div>
            <div style={styles.mobileMenuContent}>
              {Object.entries(navigationItems).map(([key, section]) => (
                <MobileNavSection 
                  key={key} 
                  title={section.label} 
                  items={section.items} 
                  path={section.path} 
                  onClose={onClose} 
                />
              ))}
              <a 
                href="/sign-in" 
                style={{
                  ...styles.mobileSignInLink, 
                  color: hoveredSignIn ? colors.orange : colors.blue
                }} 
                onClick={(e) => {
                  handleNavigation('/sign-in', e);
                  onClose();
                }} 
                onMouseEnter={() => handleMouseEnter(setHoveredSignIn)} 
                onMouseLeave={() => handleMouseLeave(setHoveredSignIn)}
              >
                Sign In
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };
  // --- End Nested Components ---

  // --- Main Header Return ---
  return (
    <>
      {/* Optional Utility Header */}
      {/* <div style={styles.utilityHeader}> ... </div> */}

      {/* Main Header */}
      <header style={styles.mainHeader} role="banner">
        <div style={styles.mainContainer}>
          {/* Logo */}
          <a 
            href="/" 
            style={styles.logoContainer} 
            title="Plastic Analytics Home"
            onClick={(e) => handleNavigation('/', e)}
          >
            <motion.img 
              src={plasticAnalyticsLogo} 
              alt="Plastic Analytics Logo" 
              style={styles.logo} 
              whileHover={{ scale: 1.05 }} 
              transition={{ type: "spring", stiffness: 400, damping: 10 }} 
            />
            <span style={styles.logoText}>Plastic Analytics™</span>
          </a>

          {/* Desktop Navigation */}
          <div style={styles.nav}>
              <nav style={styles.navLinks} ref={dropdownRef} aria-label="Main navigation">
                {Object.entries(navigationItems).map(([key, section]) => (
                  <DropdownMenu 
                    key={key} 
                    section={section} 
                    isActive={key === activeSectionKey} 
                    activeDropdown={activeDropdown} 
                    setActiveDropdown={setActiveDropdown}
                  />
                ))}
              </nav>
          </div>

          {/* Actions: Sign In Button (Desktop) / Hamburger (Mobile) */}
          <div style={styles.actionsContainer}>
              <motion.button 
                style={
                  hoveredButton === 'signin' ? 
                  {...styles.signInButtonBase, ...styles.signInButtonHover} : 
                  styles.signInButtonBase
                } 
                onMouseEnter={() => windowWidth > mobileBreakpoint && setHoveredButton('signin')} 
                onMouseLeave={() => windowWidth > mobileBreakpoint && setHoveredButton(null)} 
                onClick={(e) => handleNavigation('/sign-in', e)}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
              >
                Sign In
              </motion.button>
              <motion.button 
                style={
                  hoveredButton === 'hamburger' ? 
                  {...styles.hamburger, ...styles.hamburgerHover} : 
                  styles.hamburger
                } 
                onMouseEnter={() => windowWidth > mobileBreakpoint && setHoveredButton('hamburger')} 
                onMouseLeave={() => windowWidth > mobileBreakpoint && setHoveredButton(null)} 
                whileTap={{ scale: 0.9 }} 
                onClick={handleMenuToggle} 
                aria-label="Open menu" 
                aria-expanded={isMenuOpen}
              >
                <span>&#9776;</span>
              </motion.button>
          </div>
        </div>
      </header>

      {/* Render Mobile menu */}
      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      {/* Spacer div */}
      <div style={{ height: windowWidth > mobileBreakpoint ? '100px' : '80px' }}></div>
    </>
  );
};

export default Header;